import { useMemo } from 'react';
import type { Folder, MessageSummary, NotificationRule } from '@/api';
import { Menu, type MenuActionNode, type MenuAnchor, type MenuNode, type MenuVariant } from './Menu';
import { OFFLINE_TRANSFER_REASON, type DropMode } from '@/features/dnd/messageDrag';
import { flattenMoveTargets, mailboxMoveItems, type MailboxFolders } from './moveTargets';

/**
 * The message row's verb list (ADR 0019 §2.1).
 *
 * One implementation per verb, three ways in: this component only *names* the
 * verbs — every `actions` callback is the same function the keymap and the
 * toolbar call, passed down from `MailView`.
 *
 * Items that do not apply are **absent, not disabled**: a greyed-out "Archive"
 * in the Archive folder teaches nothing. Forward, Copy-to and Download
 * original are present only when the caller passes the action, so a screen
 * that has not wired them shows no dead item.
 *
 * The Notify group (ADR 0021 §2.1) is present now that
 * `POST /notification-rules` is real. Each item is **one POST or one DELETE**,
 * not a dialog: the sender is already in view, the rule is one row, and the
 * result shows up in Settings → Notifications the next time it is opened. What
 * the rule *means* is the server's to decide — nothing here evaluates one.
 */

export interface MessageMenuActions {
  open: () => void;
  openInWindow: () => void;
  /** Toggles `\Seen` on the target. */
  toggleRead: () => void;
  toggleStar: () => void;
  archive: () => void;
  move: (folderId: string) => void;
  /**
   * Copy to another folder, leaving the original where it is. Optional: a
   * caller that has not wired it gets no Copy-to submenu rather than one that
   * does nothing.
   */
  copyTo?: (folderId: string) => void;
  /**
   * Move or copy into a folder of ANOTHER mailbox (ADR 0108). With it and more
   * than one entry in `mailboxes`, Move to and Copy to list every mailbox's
   * folders, grouped by mailbox.
   */
  transfer?: (accountId: string, folderId: string, mode: DropMode) => void;
  /** Download the message as a `.eml`. Optional, like `copyTo`. */
  downloadOriginal?: () => void;
  remove: () => void;
  /**
   * Report spam (`!`). Optional, and passed only where it can work: a mailbox
   * with no Junk folder, or a row already in Junk, gets no item at all.
   */
  reportSpam?: () => void;
  /** Not spam (`!` in Junk): back to the Inbox. Passed instead of `reportSpam`. */
  notSpam?: () => void;
  /** "Block sender…": opens the confirmation. Optional, like `copyTo`. */
  blockSender?: () => void;
  /** "Add to Tasks" (ADR 0075): opens the form. Optional, like `copyTo`. */
  addToTasks?: () => void;
  /** "Snooze…" (ADR 0076): opens the chooser. Absent where mail cannot be snoozed. */
  snooze?: () => void;
  /** "Label as…" (plan/17): opens the label picker. Optional, like `copyTo`. */
  labelAs?: () => void;
  reply: () => void;
  replyAll: () => void;
  /**
   * Forward the target, files and all. Optional, like `copyTo`. Unlike Reply
   * it needs nothing from the cache: the server builds the forward from the
   * message itself, so a row the user never opened forwards just as well.
   */
  forward?: () => void;
  /** One POST: always notify me about this sender's address. */
  alwaysNotify: () => void;
  /** One POST: never notify me about this sender's address. */
  muteSender: () => void;
  /** One DELETE: drop whichever rule currently covers this sender. */
  clearNotifyRule: (ruleId: string) => void;
}

export interface MessageContextMenuProps {
  message: MessageSummary;
  folders: Folder[];
  anchor: MenuAnchor;
  /** The grid container — where focus goes on close, never a row. */
  returnFocusTo: HTMLElement | null;
  onClose: () => void;
  actions: MessageMenuActions;
  /** Open the Move-to submenu straight away; this is what `V` does. */
  openMoveTo?: boolean;
  /**
   * The rule that already covers this sender's address, if any, so the group
   * offers "Stop notifying" / "Unmute" instead of a duplicate that would 409.
   */
  notifyRule?: NotificationRule | undefined;
  /**
   * `sheet` on handheld (ADR 0048 §6): the same verbs as a bottom sheet of
   * 48-px rows, with Move to as a second sheet. "Open in new window" is absent
   * there — the popup window of ADR 0020 is a desktop affordance, and on a
   * phone it would be a second in-app view of the same message.
   */
  variant?: MenuVariant;
  /**
   * Every mailbox's folders, this message's own included (ADR 0108). Only
   * used with `actions.transfer` and more than one mailbox; otherwise the
   * lists are `folders` alone, exactly as before.
   */
  mailboxes?: readonly MailboxFolders[] | undefined;
  /** Offline, the other mailboxes' folders are offered but disabled. */
  online?: boolean;
}

export const MOVE_TO_SUBMENU_ID = 'move-to';
export const COPY_TO_SUBMENU_ID = 'copy-to';

export function MessageContextMenu({
  message,
  folders,
  anchor,
  returnFocusTo,
  onClose,
  actions,
  openMoveTo = false,
  notifyRule,
  variant = 'popover',
  mailboxes,
  online = true,
}: MessageContextMenuProps) {
  const unread = !message.flags.includes('seen');
  const flagged = message.flags.includes('flagged');
  const archiveFolder = folders.find((folder) => folder.role === 'archive');
  const inArchive = archiveFolder !== undefined && archiveFolder.id === message.folderId;

  const items = useMemo<MenuNode[]>(() => {
    const moveTargets = flattenMoveTargets(folders, message.folderId);

    const nodes: MenuNode[] = [
      { kind: 'item', id: 'open', label: 'Open', shortcut: 'Enter', onSelect: actions.open },
      ...(variant === 'sheet'
        ? []
        : [
            {
              kind: 'item' as const,
              id: 'open-window',
              label: 'Open in new window',
              shortcut: 'Shift+Enter',
              onSelect: actions.openInWindow,
            },
          ]),
      { kind: 'separator', id: 'sep-state' },
      {
        kind: 'item',
        id: 'read',
        label: unread ? 'Mark as read' : 'Mark as unread',
        shortcut: unread ? 'Shift+I' : 'Shift+U',
        onSelect: actions.toggleRead,
      },
      {
        kind: 'item',
        id: 'star',
        label: flagged ? 'Remove star' : 'Star',
        shortcut: 'S',
        onSelect: actions.toggleStar,
      },
      ...(actions.addToTasks !== undefined
        ? [
            {
              kind: 'item' as const,
              id: 'add-to-tasks',
              label: 'Add to Tasks',
              shortcut: 'Shift+T',
              onSelect: actions.addToTasks,
            },
          ]
        : []),
      ...(actions.snooze !== undefined
        ? [{ kind: 'item' as const, id: 'snooze', label: 'Snooze…', shortcut: 'B', onSelect: actions.snooze }]
        : []),
      ...(actions.labelAs !== undefined
        ? [{ kind: 'item' as const, id: 'label-as', label: 'Label as…', shortcut: 'L', onSelect: actions.labelAs }]
        : []),
      { kind: 'separator', id: 'sep-file' },
    ];

    // Absent, not disabled: archiving a message that is already in Archive is
    // not a thing the user can mean — and on a mailbox with no Archive folder
    // at all there is nowhere for the verb to go. The backend answers that
    // case 404 `no_archive_folder`, so an item offered there could only remove
    // the row and then put it back.
    if (archiveFolder !== undefined && !inArchive) {
      nodes.push({ kind: 'item', id: 'archive', label: 'Archive', shortcut: 'E', onSelect: actions.archive });
    }

    // Several mailboxes: every mailbox's folders, grouped (ADR 0108). A folder
    // of this message's own mailbox is the ordinary move; any other is a
    // transfer.
    const transfer = actions.transfer;
    const grouped = transfer !== undefined && mailboxes !== undefined && mailboxes.length > 1;
    const across = (mode: DropMode, current: string | undefined): MenuActionNode[] =>
      mailboxMoveItems({
        mailboxes: mailboxes ?? [],
        sources: new Set([message.accountId]),
        current: { accountId: message.accountId, folderId: current },
        online,
        offlineReason: OFFLINE_TRANSFER_REASON,
        idPrefix: mode,
      }).map(({ accountId, folderId, ...item }) => ({
        kind: 'item' as const,
        ...item,
        onSelect: () => {
          if (accountId !== message.accountId) transfer?.(accountId, folderId, mode);
          else if (mode === 'move') actions.move(folderId);
          else actions.copyTo?.(folderId);
        },
      }));

    const moveItems: MenuActionNode[] = grouped
      ? across('move', message.folderId)
      : moveTargets.map((target) => ({
          kind: 'item' as const,
          id: `move-${target.folder.id}`,
          label: target.folder.name,
          depth: target.depth,
          onSelect: () => actions.move(target.folder.id),
        }));
    if (moveItems.length > 0) {
      nodes.push({
        kind: 'submenu',
        id: MOVE_TO_SUBMENU_ID,
        label: 'Move to',
        shortcut: 'V',
        filterLabel: 'Filter folders',
        items: moveItems,
      });
    }

    // Copy targets include the current folder: copying a message into the
    // folder it is already in is a real thing to want (a second copy to keep),
    // which is exactly why it is not the same list as Move.
    const copyTo = actions.copyTo;
    if (copyTo !== undefined) {
      const copyItems: MenuActionNode[] = grouped
        ? across('copy', undefined)
        : flattenMoveTargets(folders, undefined).map((target) => ({
            kind: 'item' as const,
            id: `copy-${target.folder.id}`,
            label: target.folder.name,
            depth: target.depth,
            onSelect: () => copyTo(target.folder.id),
          }));
      if (copyItems.length > 0) {
        nodes.push({
          kind: 'submenu',
          id: COPY_TO_SUBMENU_ID,
          label: 'Copy to',
          filterLabel: 'Filter folders',
          items: copyItems,
        });
      }
    }

    nodes.push({ kind: 'item', id: 'delete', label: 'Delete', shortcut: 'Del', onSelect: actions.remove });
    if (actions.notSpam !== undefined) {
      nodes.push({ kind: 'item', id: 'not-spam', label: 'Not spam', shortcut: '!', onSelect: actions.notSpam });
    } else if (actions.reportSpam !== undefined) {
      nodes.push({
        kind: 'item',
        id: 'report-spam',
        label: 'Report spam',
        shortcut: '!',
        onSelect: actions.reportSpam,
      });
    }
    nodes.push(
      { kind: 'separator', id: 'sep-respond' },
      { kind: 'item', id: 'reply', label: 'Reply', shortcut: 'R', onSelect: actions.reply },
      { kind: 'item', id: 'reply-all', label: 'Reply all', shortcut: 'A', onSelect: actions.replyAll },
    );
    if (actions.forward !== undefined) {
      nodes.push({ kind: 'item', id: 'forward', label: 'Forward', shortcut: 'F', onSelect: actions.forward });
    }

    if (actions.downloadOriginal !== undefined) {
      nodes.push(
        { kind: 'separator', id: 'sep-source' },
        {
          kind: 'item',
          id: 'download-original',
          label: 'Download original (.eml)',
          onSelect: actions.downloadOriginal,
        },
      );
    }

    // Notify group (ADR 0021). Only for a sender we actually have an address
    // for; a rule keyed on an empty string is a rule that can never match.
    const address = message.from.email.trim();
    if (address !== '') {
      nodes.push({ kind: 'separator', id: 'sep-notify' });
      // Block sender sits with the other per-address verbs: it, too, is a
      // standing rule about this sender rather than an action on this message.
      if (actions.blockSender !== undefined) {
        nodes.push({ kind: 'item', id: 'block-sender', label: 'Block sender…', onSelect: actions.blockSender });
      }
      if (notifyRule?.action === 'notify') {
        nodes.push({
          kind: 'item',
          id: 'notify-stop',
          label: `Stop notifying me about ${address}`,
          onSelect: () => actions.clearNotifyRule(notifyRule.id),
        });
      } else if (notifyRule?.action === 'mute') {
        nodes.push({
          kind: 'item',
          id: 'notify-unmute',
          label: `Unmute ${address}`,
          onSelect: () => actions.clearNotifyRule(notifyRule.id),
        });
      } else {
        nodes.push(
          {
            kind: 'item',
            id: 'notify-always',
            label: `Always notify me about ${address}`,
            onSelect: actions.alwaysNotify,
          },
          {
            kind: 'item',
            id: 'notify-mute',
            label: `Mute ${address}`,
            onSelect: actions.muteSender,
          },
        );
      }
    }

    return nodes;
  }, [
    actions,
    archiveFolder,
    flagged,
    folders,
    inArchive,
    mailboxes,
    message.accountId,
    message.folderId,
    message.from.email,
    notifyRule,
    online,
    unread,
    variant,
  ]);

  return (
    <Menu
      label={`Actions for ${message.subject || '(no subject)'}`}
      items={items}
      anchor={anchor}
      returnFocusTo={returnFocusTo}
      onClose={onClose}
      variant={variant}
      {...(openMoveTo ? { openSubmenuId: MOVE_TO_SUBMENU_ID } : {})}
    />
  );
}
